import {
    useCallback, useEffect, useMemo, useRef, useState
} from 'react';


export function setupCarouselArrowFocus(delay = 300, containerSelector = '.react-multi-carousel-list') {
    const timer = setTimeout(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                setTimeout(() => {
                    
                    const isSFv2Container = !!document.querySelector('.app-body-v2');
                    if (!isSFv2Container) return;

                    
                    let container = document.activeElement;
                    while (container && !container.classList?.contains('react-multi-carousel-list')) {
                        container = container.parentElement;
                    }
                    
                    if (!container) {
                        container = e.target.closest('.react-multi-carousel-list');
                    }
                    if (!container) return;

                    const arrowSelector = e.key === 'ArrowLeft'
                        ? '.react-multiple-carousel__arrow--left'
                        : '.react-multiple-carousel__arrow--right';
                    const arrow = container.querySelector(arrowSelector);
                    if (arrow) arrow.focus();
                }, 0);
            }
        };

        
        const containers = document.querySelectorAll(containerSelector);
        containers.forEach((container) => {
            container.addEventListener('keydown', handleKeyDown);
        });

        
        return () => {
            containers.forEach((container) => {
                container.removeEventListener('keydown', handleKeyDown);
            });
        };
    }, delay);

    
    return () => clearTimeout(timer);
}



export function setupCarouselTabFocus(arrowKeyNavRef, containerSelector = '.react-multi-carousel-list') {
    const containers = document.querySelectorAll(containerSelector);

    function getFocusableElements(container) {
        const visibleSlides = container.querySelectorAll(
            '.react-multi-carousel-item--active:not(.react-multi-carousel-item--cloned)'
        );
        const focusableSelectors = '[tabindex], a, button, input, [role="button"]';
        const focusables = [];
        visibleSlides.forEach((slide) => {
            slide.querySelectorAll(focusableSelectors).forEach(el => focusables.push(el));
        });
        return focusables;
    }

    function handleForwardTab(e, container, last) {
        if (!e.shiftKey && document.activeElement === last) {
            const rightArrow = container.querySelector('.react-multiple-carousel__arrow--right');
            
            if (rightArrow && !rightArrow.disabled && !rightArrow.getAttribute('aria-disabled')) {
                e.preventDefault();
                rightArrow.click();
            }
            
        }
    }

    function handleReverseTab(e, container, first) {
        if (e.shiftKey && document.activeElement === first) {
            const leftArrow = container.querySelector('.react-multiple-carousel__arrow--left');
            if (leftArrow && !leftArrow.disabled && !leftArrow.getAttribute('aria-disabled')) {
                e.preventDefault();
                leftArrow.click();
            }
        }
    }

    function handleTabNavigation(e, container) {
        const focusables = getFocusableElements(container);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        handleForwardTab(e, container, last);
        handleReverseTab(e, container, first);
    }

    function handleKeyDown(e) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            arrowKeyNavRef.current = true;
        }

        if (e.key === 'Tab') {
            const container = e.currentTarget;
            handleTabNavigation(e, container);
        }
    }

    containers.forEach((container) => {
        container.addEventListener('keydown', handleKeyDown);
    });

    return () => {
        containers.forEach((container) => {
            container.removeEventListener('keydown', handleKeyDown);
        });
    };
}

function getRenderedItemPitch(list) {
    const items = list ? list.querySelectorAll('.react-multi-carousel-item') : [];
    return items.length > 1 ? items[1].offsetLeft - items[0].offsetLeft : 0;
}

function isCarouselFitted(carousel) {
    const pitch = getRenderedItemPitch(carousel.containerRef?.current);
    return !pitch || Math.abs(pitch - carousel.state.itemWidth) <= 2;
}


const ARROW_INSET_SLOTS = 0.4;


function getCenteringSlide(index, itemCount, slidesToShow) {
    const maxSlide = Math.max(itemCount - slidesToShow, 0);
    return Math.min(Math.max(index - (slidesToShow - 1) / 2, 0), maxSlide);
}

function isSlotFullyVisible(index, itemCount, { currentSlide, slidesToShow }) {
    const maxSlide = Math.max(itemCount - slidesToShow, 0);
    const position = index - currentSlide;
    const leftLimit = currentSlide > 0 ? ARROW_INSET_SLOTS : 0;
    const rightLimit = slidesToShow - (currentSlide < maxSlide ? ARROW_INSET_SLOTS : 0);
    return position >= leftLimit && position + 1 <= rightLimit;
}

export function useCarouselFitToItemWidth(carouselRef, responsive) {
    const [itemsThatFit, setItemsThatFit] = useState(null);

    const measure = useCallback(() => {
        const list = carouselRef?.current?.containerRef?.current;
        const pitch = getRenderedItemPitch(list);
        if (!list?.offsetWidth || pitch <= 0) return;
        const raw = list.offsetWidth / pitch;
        
        const fitted = Math.abs(raw - Math.round(raw)) < 0.05 ? Math.round(raw) : Math.floor(raw * 100) / 100;
        const next = Math.max(fitted, 1);
        setItemsThatFit(prev => (prev !== null && Math.abs(prev - next) < 0.01 ? prev : next));
    }, [carouselRef]);

    useEffect(measure);

    useEffect(() => {
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [measure]);

    const fittedResponsive = useMemo(() => {
        if (itemsThatFit === null) return responsive;
        return Object.keys(responsive).reduce((acc, key) => ({
            ...acc,
            [key]: { ...responsive[key], items: itemsThatFit }
        }), {});
    }, [responsive, itemsThatFit]);

    
    useEffect(() => {
        const carousel = carouselRef?.current;
        if (itemsThatFit !== null && typeof carousel?.setItemsToShow === 'function') {
            carousel.setItemsToShow(true);
        }
    }, [fittedResponsive]);

    return fittedResponsive;
}

export function useCarouselRovingFocus(itemCount, selectedIndex, carouselRef) {
    const [focusIndex, setFocusIndex] = useState(selectedIndex >= 0 ? selectedIndex : 0);
    const buttonRefs = useRef([]);
    const refCallbacks = useRef(new Map());

    
    useEffect(() => {
        if (selectedIndex >= 0) {
            setFocusIndex(selectedIndex);
        }
    }, [selectedIndex]);

    
    useEffect(() => {
        setFocusIndex(prev => Math.min(prev, Math.max(itemCount - 1, 0)));
    }, [itemCount]);

    const setButtonRef = useCallback((index) => {
        if (!refCallbacks.current.has(index)) {
            refCallbacks.current.set(index, (node) => {
                buttonRefs.current[index] = node;
            });
        }
        return refCallbacks.current.get(index);
    }, []);

    
    const centeredCarousel = useRef(null);
    const centerRetryTimer = useRef(null);
    useEffect(() => {
        const tryCenter = (attemptsLeft) => {
            centerRetryTimer.current = null;
            const carousel = carouselRef?.current;
            if (!carousel || carousel === centeredCarousel.current || selectedIndex < 0) return;
            if (typeof carousel.goToSlide !== 'function') return;
            
            const itemWidth = carousel.state?.itemWidth;
            if (!itemWidth || (attemptsLeft > 0 && !isCarouselFitted(carousel))) {
                if (attemptsLeft > 0) {
                    centerRetryTimer.current = setTimeout(() => tryCenter(attemptsLeft - 1), 100);
                }
                return;
            }
            centeredCarousel.current = carousel;
            const targetSlide = getCenteringSlide(selectedIndex, itemCount, carousel.state.slidesToShow);
            if (targetSlide !== carousel.state.currentSlide) {
                carousel.goToSlide(targetSlide, { skipAfterChange: true }, false);
            }
        };
        if (!centerRetryTimer.current) tryCenter(10);
    });

    useEffect(() => () => clearTimeout(centerRetryTimer.current), []);

    const ensureVisible = (index) => {
        const carousel = carouselRef?.current;
        if (typeof carousel?.goToSlide !== 'function' || !carousel.state?.slidesToShow) return;
        if (isSlotFullyVisible(index, itemCount, carousel.state)) return;
        if (carousel.isInThrottle) {
            
            setTimeout(() => {
                if (document.activeElement === buttonRefs.current[index]) ensureVisible(index);
            }, (carousel.props.transitionDuration || 400) + 50);
            return;
        }
        carousel.goToSlide(
            getCenteringSlide(index, itemCount, carousel.state.slidesToShow),
            { skipAfterChange: true }
        );
    };

    const handleFocus = (index) => {
        setFocusIndex(index);
        
        const node = buttonRefs.current[index];
        const list = node && node.closest && node.closest('.react-multi-carousel-list');
        if (list && list.scrollLeft) list.scrollLeft = 0;
        ensureVisible(index);
    };

    const focusIndexAt = (index) => {
        setFocusIndex(index);
        const node = buttonRefs.current[index];
        if (node) node.focus({ preventScroll: true });
    };

    const handleArrowKeyDown = (event, index) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const nextIndex = event.key === 'ArrowRight' ? index + 1 : index - 1;
        if (nextIndex < 0 || nextIndex >= itemCount) return;
        event.preventDefault();
        focusIndexAt(nextIndex);
    };

    const getTabIndex = index => (index === focusIndex ? 0 : -1);

    return {
        getTabIndex, setButtonRef, handleArrowKeyDown, handleFocus
    };
}
